import { useEffect, useMemo, useState } from 'react';
import type { ProjectPlan, ProjektVerortung } from '../../types';
import { standortVerfuegbar, useStandortSensoren } from '../../services/standortSensoren';
import {
    ebeneBestaetigen, ebeneSchaetzen, geschossEbenen, planFuerEbene, schnappschussBilden, useVerortungGeraet,
    useVerortungsSitzung, verortungNutzbar, type EbenenSchaetzung, type SensorSchnappschuss,
} from '../../services/verortung';
import { livePunktFuerPlan, type LivePunkt } from '../../services/verortung/liveStandort';

/**
 * Live-Standort für Planansichten (Wizard-Planwahl, „Verortung auf Plan“):
 * Sensoren nur, solange `offen` UND Gerät, Projekt und Einblendung es erlauben
 * (docs/verortung-kamera-vertrag.md „Abschaltbar auf drei Ebenen“).
 *
 * Liefert je Plan den Live-Punkt, die Ebenen-Schätzung, den Plan der
 * geschätzten Ebene und `planBestaetigen` — Tippen auf einen Geschossplan ist
 * die Korrektur „hier bin ich“ und nullt das Barometer neu.
 */

export interface LiveStandort {
    /** Läuft die Live-Anzeige (alle Schalter an, App mit Sensoren, Ansicht offen)? */
    aktiv: boolean;
    /** Wäre sie möglich (App + Gerät + Projekt), nur die Einblendung ist aus? */
    abschaltbar: boolean;
    einblendungAn: boolean;
    einblendungUmschalten: () => void;
    schnappschuss: SensorSchnappschuss | null;
    schaetzung: EbenenSchaetzung | null;
    /** Plan der geschätzten Ebene (oder null). */
    livePlanId: string | null;
    punktFuer: (planId: string) => LivePunkt | null;
    /** Tipp auf den Plan eines Geschosses = Bestätigung „hier bin ich“ (neuer Nullpunkt). */
    planBestaetigen: (planId: string) => void;
    fehler: string | null;
}

export function useLiveStandort(opts: {
    offen: boolean;
    verortung: ProjektVerortung;
    plaene: readonly ProjectPlan[];
    sitzungSchluessel: string;
}): LiveStandort {
    const { offen, verortung, plaene, sitzungSchluessel } = opts;
    const [geraet, geraetSetzen] = useVerortungGeraet();
    const [sensorApp] = useState<boolean>(standortVerfuegbar);
    const moeglich = verortungNutzbar(geraet, verortung, sensorApp);
    const aktiv = offen && moeglich && geraet.einblendung;
    const sensoren = useStandortSensoren(aktiv);
    const [sitzung, setSitzung] = useVerortungsSitzung(sitzungSchluessel);
    const [jetzt, setJetzt] = useState(0);

    // Uhr nur bei laufender Anzeige: Median-Fenster und GPS-Fenster wandern mit.
    useEffect(() => {
        if (!aktiv) return undefined;
        const tick = () => setJetzt(Date.now());
        const erster = window.setTimeout(tick, 0);
        const takt = window.setInterval(tick, 500);
        return () => { window.clearTimeout(erster); window.clearInterval(takt); };
    }, [aktiv]);

    const schnappschuss = useMemo<SensorSchnappschuss | null>(() => {
        if (!aktiv || jetzt === 0) return null;
        const letzteProbe = sensoren.druckProben.length ? sensoren.druckProben[sensoren.druckProben.length - 1].t : 0;
        return schnappschussBilden(sensoren.druckProben, sensoren.positionen, sensoren.richtung, Math.max(jetzt, letzteProbe));
    }, [aktiv, jetzt, sensoren.druckProben, sensoren.positionen, sensoren.richtung]);

    const schaetzung = schnappschuss && schnappschuss.druckHpa !== null
        ? ebeneSchaetzen(verortung.geschosse, verortung.geruest, sitzung, schnappschuss.druckHpa, schnappschuss.t)
        : null;
    const livePlanId = schaetzung && !schaetzung.zwischen
        ? planFuerEbene(verortung, plaene, schaetzung.ebene)?.id ?? null
        : null;

    const punktFuer = (planId: string): LivePunkt | null =>
        schnappschuss ? livePunktFuerPlan(verortung.plaene?.[planId], schnappschuss) : null;

    const planBestaetigen = (planId: string): void => {
        if (!aktiv || !schnappschuss || schnappschuss.druckHpa === null) return;
        const vp = verortung.plaene?.[planId];
        if (!vp || vp.art === 'lageplan' || !vp.geschossId) return;
        const ebene = geschossEbenen(verortung.geschosse).find((e) => e.id === vp.geschossId);
        if (ebene) setSitzung(ebeneBestaetigen(sitzung, ebene, schnappschuss.druckHpa, Date.now()));
    };

    return {
        aktiv,
        abschaltbar: offen && moeglich,
        einblendungAn: geraet.einblendung,
        einblendungUmschalten: () => geraetSetzen({ einblendung: !geraet.einblendung }),
        schnappschuss,
        schaetzung,
        livePlanId,
        punktFuer,
        planBestaetigen,
        fehler: sensoren.fehler,
    };
}
